import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWishes } from '../api/wishes';
import { getMoments } from '../api/moments';

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function groupByMonth(items, dateKey) {
  const groups = {};
  items.forEach(item => {
    const date = item[dateKey] ? new Date(item[dateKey]) : null;
    const key = date
      ? date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : 'No date';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return Object.entries(groups);
}

function FriendProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [wishes, setWishes] = useState([]);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = () => localStorage.getItem('auth_token');

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin'); return; }
    fetchData();
  }, [id, isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [wishData, momentData] = await Promise.all([
        getWishes(token(), null, 1, id),
        getMoments(token(), null, 1, id)
      ]);
      setWishes(wishData.wishes || []);
      setMoments(momentData.moments || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You are not friends with this user.');
      } else {
        setError('Could not load this profile.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;
  if (loading) return <div className="loading">Loading profile...</div>;

  if (error) return (
    <div className="friend-profile">
      <p className="error">{error}</p>
      <Link to="/friends" className="btn btn-secondary">← Back to friends</Link>
    </div>
  );

  const previewWishes  = wishes.slice(0, 2);
  const previewMoments = moments.slice(0, 2);

  return (
    <div className="friend-profile">
      <Link to="/friends" className="friend-profile__back">← Back to friends</Link>
      <h1 className="friend-profile__title">Friend's profile</h1>

      {/* ── WISHES preview ── */}
      <section className="friend-profile__section">
        <div className="friend-profile__section-header">
          <Link to={`/friends/${id}/wishes`} className="friend-profile__section-link">
            Wishes ({wishes.length}) →
          </Link>
        </div>
        {wishes.length === 0 ? (
          <p className="friend-profile__empty">No wishes yet.</p>
        ) : (
          <div className="wish-cards">
            {previewWishes.map(wish => (
              <div key={wish.id} className="wish-card">
                {wish.event_url_image && (
                  <div className="wish-card__image-wrapper">
                    <img src={wish.event_url_image} alt={wish.event_name} className="wish-card__image" />
                  </div>
                )}
                <div className="wish-card__body">
                  <div className="wish-card__meta">
                    {wish.event_category && <span className="wish-card__category">{wish.event_category}</span>}
                    {wish.event_date && <span className="wish-card__date">📅 {formatDate(wish.event_date)}</span>}
                  </div>
                  <h2 className="wish-card__title">
                    <Link to={`/events/${wish.event_id}`}>{wish.event_name || `Event #${wish.event_id}`}</Link>
                  </h2>
                  {wish.event_description && (
                    <p className="wish-card__description">
                      {wish.event_description.length > 120 ? wish.event_description.slice(0, 120) + '…' : wish.event_description}
                    </p>
                  )}
                  <div className="wish-card__actions">
                    <Link to={`/events/${wish.event_id}`} className="btn btn-primary btn-sm">View event</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {wishes.length > 2 && (
          <Link to={`/friends/${id}/wishes`} className="friend-profile__see-all">
            See all {wishes.length} wishes →
          </Link>
        )}
      </section>

      {/* ── MOMENTS preview ── */}
      <section className="friend-profile__section">
        <div className="friend-profile__section-header">
          <Link to={`/friends/${id}/moments`} className="friend-profile__section-link">
            Moments ({moments.length}) →
          </Link>
        </div>
        {moments.length === 0 ? (
          <p className="friend-profile__empty">No moments yet.</p>
        ) : (
          <div className="moment-grid">
            {previewMoments.map(moment => (
              <Link key={moment.id} to={`/moments/${moment.id}`} className="moment-grid__cell">
                {moment.event_url_image ? (
                  <img src={moment.event_url_image} alt={moment.event_name} className="moment-grid__img" />
                ) : (
                  <div className="moment-grid__img moment-grid__img--placeholder" />
                )}
                <div className="moment-grid__caption">
                  <p className="moment-grid__caption-title">{moment.event_name}</p>
                </div>
                {moment.description && (
                  <div className="moment-grid__hover">
                    <p className="moment-grid__hover-desc">
                      {moment.description.length > 80 ? moment.description.slice(0, 80) + '…' : moment.description}
                    </p>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
        {moments.length > 2 && (
          <Link to={`/friends/${id}/moments`} className="friend-profile__see-all">
            See all {moments.length} moments →
          </Link>
        )}
      </section>
    </div>
  );
}

export default FriendProfile;
